import React, { useEffect, useState, useCallback } from "react";
import { Clock, Users, Phone, RefreshCw } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import { Spinner, EmptyState } from "../../components/common/UI";
import toast from "react-hot-toast";

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    confirmed: "bg-green-100 text-green-800",
    cancelled: "bg-red-100 text-red-800",
  };
  return <span className={`badge ${map[status] || ""} capitalize`}>{status}</span>;
};

export default function RestaurantBookings() {
  const { user } = useAuth();
  const [restaurantId, setRestaurantId] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    supabase
      .from("restaurants")
      .select("id")
      .eq("owner_id", user.id)
      .single()
      .then(({ data }) => { if (data) setRestaurantId(data.id); });
  }, [user]);

  const fetchBookings = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    let query = supabase
      .from("bookings")
      .select("*")
      .eq("restaurant_id", restaurantId)
      .order("date", { ascending: true })
      .order("time", { ascending: true });
    if (filter !== "all") query = query.eq("status", filter);
    const { data } = await query;
    setBookings(data || []);
    setLoading(false);
  }, [restaurantId, filter]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
    if (error) { toast.error("Failed to update"); return; }
    setBookings((p) => p.map((b) => b.id === id ? { ...b, status } : b));
    toast.success(`Booking ${status}`);
  };

  // Group bookings by date
  const grouped = bookings.reduce((acc, booking) => {
    const date = booking.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(booking);
    return acc;
  }, {});

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Table Bookings</h1>
          <p className="text-gray-500 text-sm">{bookings.length} booking(s)</p>
        </div>
        <button
          onClick={fetchBookings}
          className="flex items-center gap-2 btn-ghost border border-gray-200"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {["all", "pending", "confirmed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-full text-sm font-medium capitalize border transition-colors ${
              filter === s
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-200 text-gray-600 hover:border-blue-300"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : bookings.length === 0 ? (
        <EmptyState icon="🪑" title="No bookings yet" description="Bookings from customers will appear here." />
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, dayBookings]) => (
            <div key={date}>
              {/* Date header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-orange-500 text-white text-sm font-bold px-3 py-1.5 rounded-xl">
                  {new Date(date).toLocaleDateString("en-PK", {
                    weekday: "long", day: "numeric", month: "long"
                  })}
                </div>
                <span className="text-xs text-gray-400">{dayBookings.length} booking(s)</span>
              </div>

              <div className="space-y-3">
                {dayBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <StatusBadge status={booking.status} />
                          <span className="text-xs text-gray-400 font-mono">
                            #{booking.id.slice(0, 6).toUpperCase()}
                          </span>
                        </div>

                        <p className="font-semibold text-gray-900">{booking.customer_name}</p>

                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-orange-400" />
                            <a href={`tel:${booking.customer_phone}`} className="hover:text-orange-600">
                              {booking.customer_phone}
                            </a>
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-orange-400" />
                            {booking.time}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-orange-400" />
                            {booking.guests} {booking.guests === 1 ? "guest" : "guests"}
                          </span>
                        </div>

                        {booking.notes && (
                          <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded-lg px-3 py-2">
                            📝 {booking.notes}
                          </p>
                        )}
                      </div>

                      {/* Action buttons */}
                      {booking.status === "pending" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => updateStatus(booking.id, "confirmed")}
                            className="text-xs bg-green-500 hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-xl transition-colors"
                          >
                            ✓ Confirm
                          </button>
                          <button
                            onClick={() => updateStatus(booking.id, "cancelled")}
                            className="text-xs bg-red-100 hover:bg-red-200 text-red-700 font-semibold px-4 py-2 rounded-xl transition-colors"
                          >
                            ✕ Cancel
                          </button>
                        </div>
                      )}
                      {booking.status === "confirmed" && (
                        <button
                          onClick={() => updateStatus(booking.id, "cancelled")}
                          className="text-xs bg-red-100 hover:bg-red-200 text-red-700 font-semibold px-4 py-2 rounded-xl transition-colors flex-shrink-0"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
